import axios from "axios";
import { ACTIONS, MUTATIONS } from "./types-makeup-requests";
import authModule from "../../authenticator";

const makeupRequests = {
  namespaced: true,
  
  state: {
    requests: [],
    currentRequest: null,
    loading: false,
    currentTab: 'pending', // pending, approved, rejected, all
    filters: {
      requestType: null, // null=All, 0=Session Swap, 1=MC Request
      searchQuery: '',
      dateRange: {
        start: null,
        end: null
      }
    }
  },
  
  getters: {
    getRequests: (state) => state.requests,
    getCurrentRequest: (state) => state.currentRequest,
    isLoading: (state) => state.loading,
    getCurrentTab: (state) => state.currentTab,
    getFilters: (state) => state.filters,
    
    // Filter requests based on local filters (type, search, date range)
    getFilteredRequests: (state) => {
      let filtered = [...state.requests];
      
      // Filter by request type
      if (state.filters.requestType !== null) {
        filtered = filtered.filter(req => req.crb5c_requesttype === state.filters.requestType);
      }
      
      // Filter by search query (client name or number)
      if (state.filters.searchQuery) {
        const query = state.filters.searchQuery.toLowerCase();
        filtered = filtered.filter(req => {
          const clientName = req.crb5c_ClientLookup?.crb5c_name?.toLowerCase() || '';
          const clientNo = req.crb5c_ClientLookup?.crb5c_no?.toLowerCase() || '';
          return clientName.includes(query) || clientNo.includes(query);
        });
      }
      
      // Filter by submission date range
      if (state.filters.dateRange.start) {
        const startDate = new Date(state.filters.dateRange.start);
        filtered = filtered.filter(req => new Date(req.createdon) >= startDate);
      }
      
      if (state.filters.dateRange.end) {
        const endDate = new Date(state.filters.dateRange.end);
        endDate.setHours(23, 59, 59, 999); // Include full day
        filtered = filtered.filter(req => new Date(req.createdon) <= endDate);
      }
      
      return filtered;
    },
    
    // Get status counts for badges
    getStatusCounts: (state) => {
      const counts = {
        pending: 0,
        approved: 0,
        rejected: 0,
        all: state.requests.length
      };
      
      state.requests.forEach(req => {
        if (req.crb5c_requeststatus === 0) counts.pending++;
        else if (req.crb5c_requeststatus === 1) counts.approved++;
        else if (req.crb5c_requeststatus === 2) counts.rejected++;
      });
      
      return counts;
    }
  },
  
  mutations: {
    [MUTATIONS.SET_REQUESTS](state, payload) {
      state.requests = payload;
    },
    
    [MUTATIONS.SET_LOADING](state, payload) {
      state.loading = payload;
    },
    
    [MUTATIONS.SET_CURRENT_REQUEST](state, payload) {
      state.currentRequest = payload;
    },
    
    [MUTATIONS.SET_FILTERS](state, payload) {
      state.filters = { ...state.filters, ...payload };
    },
    
    [MUTATIONS.SET_CURRENT_TAB](state, payload) {
      state.currentTab = payload;
    },
    
    [MUTATIONS.UPDATE_REQUEST_IN_LIST](state, updatedRequest) {
      const index = state.requests.findIndex(
        req => req.crb5c_fowsessionmakeupid === updatedRequest.crb5c_fowsessionmakeupid
      );
      if (index !== -1) {
        state.requests.splice(index, 1, updatedRequest);
      }
    }
  },
  
  actions: {
    async [ACTIONS.FETCH_REQUESTS]({ commit }) {
      commit(MUTATIONS.SET_LOADING, true);
      try {
        // The page mounts in parallel with the login handshake (especially when
        // opened from an email deep link) — wait until the session cookie is
        // set, or this first fetch 401s silently and the list stays empty.
        await authModule.ready();
        // Fetch everything once — the page splits by type (MC vs swap) and
        // status client-side, which keeps tab counts correct and lets
        // auto-approved swaps (status 4) show under their own tab.
        const params = new URLSearchParams();
        params.append('includeExpired', 'true');

        const response = await axios.get(`/api/makeup-requests?${params.toString()}`);
        commit(MUTATIONS.SET_REQUESTS, response.data.data || []);
        return response.data;
      } catch (error) {
        console.error('Error fetching makeup requests:', error);
        throw error;
      } finally {
        commit(MUTATIONS.SET_LOADING, false);
      }
    },
    
    async [ACTIONS.FETCH_SINGLE_REQUEST]({ commit }, requestId) {
      commit(MUTATIONS.SET_LOADING, true);
      try {
        const response = await axios.get(`/api/makeup-requests/${requestId}`);
        commit(MUTATIONS.SET_CURRENT_REQUEST, response.data.data);
        return response.data.data;
      } catch (error) {
        console.error('Error fetching request details:', error);
        throw error;
      } finally {
        commit(MUTATIONS.SET_LOADING, false);
      }
    },
    
    async [ACTIONS.APPROVE_REQUEST]({ commit }, requestId) {
      try {
        const response = await axios.patch(`/api/makeup-requests/${requestId}/approve`);
        commit(MUTATIONS.UPDATE_REQUEST_IN_LIST, response.data.data);
        return response.data;
      } catch (error) {
        console.error('Error approving request:', error);
        throw error;
      }
    },
    
    async [ACTIONS.REJECT_REQUEST]({ commit }, { requestId, reason }) {
      try {
        const response = await axios.patch(`/api/makeup-requests/${requestId}/reject`, { reason });
        commit(MUTATIONS.UPDATE_REQUEST_IN_LIST, response.data.data);
        return response.data;
      } catch (error) {
        console.error('Error rejecting request:', error);
        throw error;
      }
    },
    
    async [ACTIONS.GET_DOCUMENT](_, requestId) {
      try {
        const response = await axios.get(`/api/makeup-requests/${requestId}/document`);
        return response.data.data;
      } catch (error) {
        console.error('Error getting document:', error);
        throw error;
      }
    },

    // Read-only: will the client get a credit note for this MC (dates already
    // on a billed invoice) or not? Returns estimated amount + per-invoice lines.
    async [ACTIONS.GET_CREDIT_ESTIMATE](_, requestId) {
      try {
        const response = await axios.get(`/api/makeup-requests/${requestId}/credit-estimate`);
        return response.data.data;
      } catch (error) {
        console.error('Error getting credit estimate:', error);
        throw error;
      }
    },

    // Reference data for the staff "New Request" form. Without clientId returns
    // {clients, schedules}; with clientId returns {clientScheduleIds}.
    async [ACTIONS.GET_STAFF_META](_, clientId) {
      const params = clientId ? { clientId } : {};
      const response = await axios.get('/api/makeup-requests/staff-meta', { params });
      return response.data;
    },

    // Approver tiers, limit, and availability (Approver Settings).
    async [ACTIONS.GET_APPROVERS]() {
      const response = await axios.get('/api/makeup-requests/approvers');
      return response.data;
    },

    async [ACTIONS.SAVE_APPROVERS](_, config) {
      const response = await axios.put('/api/makeup-requests/approvers', config);
      return response.data;
    },

    // Staff create a swap or MC on a client's behalf. formData is multipart
    // (may carry MC documents).
    async [ACTIONS.STAFF_CREATE](_, formData) {
      const response = await axios.post('/api/makeup-requests/staff-create', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    }
  }
};

export default makeupRequests;
